import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaymentIcon from "@mui/icons-material/Payment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import CancelIcon from "@mui/icons-material/Cancel";
import companyApiService from "../../../services/companyApiService";
import { showErrorToast } from "../../../../utils/toast/toast";
import "./PaymentHistoryModal.scss";

interface Invoice {
  id: number;
  invoiceNumber: string;
  companyId: number;
  subscriptionId: number;
  planId: number;
  amount: number;
  currency: string;
  status: "draft" | "sent" | "paid" | "cancelled";
  issueDate: string;
  dueDate: string;
  paidDate?: string;
  createdAt: string;
}

interface Payment {
  id: number;
  invoiceId?: number;
  subscriptionId: number;
  amount: number;
  currency: string;
  paymentMethod: string;
  paymentStatus: "pending" | "completed" | "failed" | "refunded";
  transactionId: string;
  payHereOrderId?: string;
  cardHolderName?: string;
  cardNumber?: string;
  paymentDate: string;
  createdAt: string;
}

interface PaymentHistoryModalProps {
  companyId: number;
  companyName: string;
  onClose: () => void;
}

const PaymentHistoryModal: React.FC<PaymentHistoryModalProps> = ({
  companyId,
  companyName,
  onClose,
}) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"invoices" | "payments">(
    "invoices",
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invoicesRes, paymentsRes] = await Promise.all([
        companyApiService.getCompanyInvoices(companyId.toString()),
        companyApiService.getCompanyPayments(companyId.toString()),
      ]);

      setInvoices(invoicesRes.data.invoices || []);
      setPayments(paymentsRes.data.payments || []);
    } catch (err) {
      console.error("Failed to fetch payment history:", err);
      showErrorToast("Failed to fetch payment history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount: number, currency: string) => {
    return `${currency} ${parseFloat(amount.toString()).toFixed(2)}`;
  };

  const getStatusIcon = (
    status: string,
    type: "invoice" | "payment",
  ): React.ReactElement => {
    if (type === "invoice") {
      switch (status) {
        case "paid":
          return <CheckCircleIcon className="status-icon success" />;
        case "cancelled":
          return <CancelIcon className="status-icon error" />;
        case "sent":
          return <PendingIcon className="status-icon warning" />;
        default:
          return <PendingIcon className="status-icon pending" />;
      }
    } else {
      switch (status) {
        case "completed":
          return <CheckCircleIcon className="status-icon success" />;
        case "failed":
        case "refunded":
          return <CancelIcon className="status-icon error" />;
        default:
          return <PendingIcon className="status-icon pending" />;
      }
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case "paid":
      case "completed":
        return "status-success";
      case "failed":
      case "cancelled":
      case "refunded":
        return "status-error";
      case "sent":
        return "status-warning";
      default:
        return "status-pending";
    }
  };

  const totalPaid = payments
    .filter((p) => p.paymentStatus === "completed")
    .reduce((sum, p) => sum + parseFloat(p.amount.toString()), 0);

  const totalInvoiced = invoices.reduce(
    (sum, i) => sum + parseFloat(i.amount.toString()),
    0,
  );

  return (
    <div className="payment-history-modal-overlay" onClick={onClose}>
      <div
        className="payment-history-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h2>Payment History</h2>
            <p className="company-name">{companyName}</p>
          </div>
          <button className="close-btn" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Summary Stats */}
        <div className="summary-stats">
          <div className="stat-box">
            <div className="stat-value">
              {formatAmount(totalPaid, payments[0]?.currency || "LKR")}
            </div>
            <div className="stat-label">Total Paid</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">{payments.length}</div>
            <div className="stat-label">Total Payments</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">{invoices.length}</div>
            <div className="stat-label">Total Invoices</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">
              {formatAmount(totalInvoiced, invoices[0]?.currency || "LKR")}
            </div>
            <div className="stat-label">Total Invoiced</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="modal-tabs">
          <button
            className={activeTab === "invoices" ? "active" : ""}
            onClick={() => setActiveTab("invoices")}
          >
            <ReceiptIcon /> Invoices ({invoices.length})
          </button>
          <button
            className={activeTab === "payments" ? "active" : ""}
            onClick={() => setActiveTab("payments")}
          >
            <PaymentIcon /> Payments ({payments.length})
          </button>
        </div>

        <div className="modal-content">
          {loading ? (
            <div className="loading-state">Loading payment history...</div>
          ) : (
            <>
              {/* Invoices Tab */}
              {activeTab === "invoices" && (
                <div className="invoices-section">
                  {invoices.length === 0 ? (
                    <div className="empty-state">
                      <ReceiptIcon />
                      <p>No invoices found</p>
                    </div>
                  ) : (
                    <div className="history-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Invoice #</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Issue Date</th>
                            <th>Due Date</th>
                            <th>Paid Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((invoice) => (
                            <tr key={invoice.id}>
                              <td className="invoice-number">
                                {invoice.invoiceNumber}
                              </td>
                              <td className="amount">
                                {formatAmount(invoice.amount, invoice.currency)}
                              </td>
                              <td>
                                <div className="status-cell">
                                  {getStatusIcon(invoice.status, "invoice")}
                                  <span
                                    className={`status-badge ${getStatusClass(invoice.status)}`}
                                  >
                                    {invoice.status}
                                  </span>
                                </div>
                              </td>
                              <td>{formatDate(invoice.issueDate)}</td>
                              <td>{formatDate(invoice.dueDate)}</td>
                              <td>
                                {invoice.paidDate
                                  ? formatDate(invoice.paidDate)
                                  : "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Payments Tab */}
              {activeTab === "payments" && (
                <div className="payments-section">
                  {payments.length === 0 ? (
                    <div className="empty-state">
                      <PaymentIcon />
                      <p>No payments found</p>
                    </div>
                  ) : (
                    <div className="history-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Transaction ID</th>
                            <th>Amount</th>
                            <th>Method</th>
                            <th>Status</th>
                            <th>Card Details</th>
                            <th>Payment Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((payment) => (
                            <tr key={payment.id}>
                              <td className="transaction-id">
                                {payment.transactionId ||
                                  payment.payHereOrderId ||
                                  "N/A"}
                              </td>
                              <td className="amount">
                                {formatAmount(payment.amount, payment.currency)}
                              </td>
                              <td>{payment.paymentMethod || "PayHere"}</td>
                              <td>
                                <div className="status-cell">
                                  {getStatusIcon(
                                    payment.paymentStatus,
                                    "payment",
                                  )}
                                  <span
                                    className={`status-badge ${getStatusClass(payment.paymentStatus)}`}
                                  >
                                    {payment.paymentStatus}
                                  </span>
                                </div>
                              </td>
                              <td>
                                {payment.cardHolderName &&
                                payment.cardNumber ? (
                                  <div className="card-details">
                                    <div>{payment.cardHolderName}</div>
                                    <div className="card-number">
                                      **** {payment.cardNumber.slice(-4)}
                                    </div>
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td>{formatDate(payment.paymentDate)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentHistoryModal;
